import { c as _c } from "react/compiler-runtime";
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";
import classes from "./detail-section.module.scss";
export type DetailSectionLabelProps = Readonly<{
  children: ReactNode;
}>;
export type DetailSectionProps = Readonly<{
  label: ReactNode;
  children: ReactNode;
  action?: ReactNode;
}>;
export type DetailSectionsProps = HTMLAttributes<HTMLDivElement>;
export type DetailPanelProps = HTMLAttributes<HTMLDivElement>;
export type DetailPanelRowProps = HTMLAttributes<HTMLDivElement> & {
  label?: ReactNode;
  /** Rendered next to the label text, e.g. an info icon with a tooltip */
  labelSuffix?: ReactNode;
};
export type DetailPanelValueProps = HTMLAttributes<HTMLDivElement>;
export type DetailPanelDividerProps = HTMLAttributes<HTMLDivElement>;
export type DetailFieldErrorProps = HTMLAttributes<HTMLParagraphElement>;
export type DetailActionButtonProps = ButtonHTMLAttributes<HTMLButtonElement>;
export type DetailInfoIconProps = HTMLAttributes<HTMLSpanElement>;
export function DetailSectionLabel(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <p className={classes.sectionLabel}>{children}</p>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function DetailSection(t0) {
  const $ = _c(6);
  const {
    label,
    children,
    action
  } = t0;
  let t1;
  if ($[0] !== action || $[1] !== label) {
    t1 = action ? <div className={classes.sectionHeader}><DetailSectionLabel>{label}</DetailSectionLabel>{action}</div> : <DetailSectionLabel>{label}</DetailSectionLabel>;
    $[0] = action;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== t1) {
    t2 = <section className={classes.section}>{t1}{children}</section>;
    $[3] = children;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
export function DetailSections(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(classes.sections, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function DetailPanel(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(classes.panel, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function DetailPanelRow(t0) {
  const $ = _c(16);
  let children;
  let className;
  let label;
  let labelSuffix;
  let props;
  if ($[0] !== t0) {
    ({
      label,
      labelSuffix,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = label;
    $[4] = labelSuffix;
    $[5] = props;
  } else {
    children = $[1];
    className = $[2];
    label = $[3];
    labelSuffix = $[4];
    props = $[5];
  }
  let t1;
  if ($[6] !== className) {
    t1 = cn(classes.panelRow, className);
    $[6] = className;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== label || $[9] !== labelSuffix) {
    t2 = label != null && <div className={classes.panelRowLabel}><span className={classes.panelRowLabelText}>{label}</span>{labelSuffix}</div>;
    $[8] = label;
    $[9] = labelSuffix;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== children || $[12] !== props || $[13] !== t1 || $[14] !== t2) {
    t3 = <div className={t1} {...props}>{t2}{children}</div>;
    $[11] = children;
    $[12] = props;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}
export function DetailPanelValue(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(classes.panelRowValue, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function DetailPanelDivider(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(classes.panelDivider, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function DetailFieldError(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(classes.fieldError, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <p className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function DetailActionButton(t0) {
  const $ = _c(10);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      type: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const type = t1 === undefined ? "button" : t1;
  let t2;
  if ($[4] !== className) {
    t2 = cn(classes.resetLink, className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t2 || $[8] !== type) {
    t3 = <button type={type} className={t2} {...props} />;
    $[6] = props;
    $[7] = t2;
    $[8] = type;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
export function DetailInfoIcon(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(classes.infoIconButton, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <span className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}